import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Beef, Users, Clock, Trash2, MailCheck, MailWarning, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("wf_token") || "");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [slots, setSlots] = useState("");
  const [prices, setPrices] = useState({ quarter: "", half: "", whole: "", deposit_quarter: "", deposit_half: "", deposit_whole: "", typical_quarter: "", typical_half: "", typical_whole: "" });
  const [resendingId, setResendingId] = useState("");
  const [stats, setStats] = useState(null);

  const authed = axios.create({ headers: { Authorization: `Bearer ${token}` } });

  const loadDashboard = async (t = token) => {
    try {
      const client = axios.create({ headers: { Authorization: `Bearer ${t}` } });
      const [me, res, wait, avail, price, statsRes] = await Promise.all([
        client.get(`${API}/auth/me`),
        client.get(`${API}/admin/reservations`),
        client.get(`${API}/admin/waitlist`),
        axios.get(`${API}/availability`),
        axios.get(`${API}/pricing`),
        client.get(`${API}/admin/stats`),
      ]);
      setUser(me.data);
      setReservations(res.data);
      setWaitlist(wait.data);
      setAvailability(avail.data);
      setStats(statsRes.data);
      setSlots(String(avail.data.slots_total));
      setPrices({
        quarter: String(price.data.quarter), half: String(price.data.half), whole: String(price.data.whole),
        deposit_quarter: String(price.data.deposit_quarter), deposit_half: String(price.data.deposit_half), deposit_whole: String(price.data.deposit_whole),
        typical_quarter: price.data.typical_quarter, typical_half: price.data.typical_half, typical_whole: price.data.typical_whole,
      });
    } catch (e) {
      localStorage.removeItem("wf_token");
      setToken("");
      setUser(null);
    }
  };

  useEffect(() => {
    if (token) loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("wf_token", data.access_token);
      localStorage.setItem("wf_manager", "1");
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveSlots = async () => {
    try {
      const { data } = await authed.patch(`${API}/admin/availability`, { slots_total: parseInt(slots, 10) });
      setAvailability({ slots_total: data.slots_total, taken: data.taken, remaining: data.remaining });
      if (data.waitlist_notified > 0) {
        toast.success(`Shares opened up — ${data.waitlist_notified} waitlist customer${data.waitlist_notified === 1 ? "" : "s"} notified by email.`);
        const { data: wl } = await authed.get(`${API}/admin/waitlist`);
        setWaitlist(wl);
      } else {
        toast.success("Availability updated.");
      }
      if (data.waitlist_notify_failed > 0) {
        toast.error(`${data.waitlist_notify_failed} waitlist email${data.waitlist_notify_failed === 1 ? "" : "s"} failed to send. Check email settings.`);
      }
    } catch (e) {
      setError("Could not update availability.");
      toast.error("Could not update availability.");
    }
  };

  const savePrices = async () => {
    try {
      await authed.patch(`${API}/admin/pricing`, {
        quarter: parseFloat(prices.quarter),
        half: parseFloat(prices.half),
        whole: parseFloat(prices.whole),
        deposit_quarter: parseFloat(prices.deposit_quarter),
        deposit_half: parseFloat(prices.deposit_half),
        deposit_whole: parseFloat(prices.deposit_whole),
        typical_quarter: prices.typical_quarter,
        typical_half: prices.typical_half,
        typical_whole: prices.typical_whole,
      });
      setError("");
      toast.success("Pricing saved — live on the website now.");
    } catch (e) {
      setError("Could not update prices.");
    }
  };

  const markPaid = async (id) => {
    try {
      const { data } = await authed.post(`${API}/admin/reservations/${id}/mark-paid`);
      setReservations((rs) => rs.map((r) => (r.id === id ? { ...r, status: "deposit_paid", receipt_email: data.receipt_email } : r)));
      const buyer = reservations.find((r) => r.id === id);
      if (data.receipt_email === "sent") {
        toast.success(`Marked paid — receipt email sent to ${buyer?.email || "the buyer"}.`);
      } else if (data.receipt_email === "failed") {
        toast.error("Marked paid, but the receipt email failed to send. Check email settings.");
      } else {
        toast.success("Marked as paid.");
      }
    } catch (e) {
      setError("Could not mark as paid.");
      toast.error("Could not mark as paid.");
    }
  };

  const resendReceipt = async (id) => {
    if (resendingId) return;
    setResendingId(id);
    try {
      const { data } = await authed.post(`${API}/admin/reservations/${id}/resend-receipt`);
      setReservations((rs) => rs.map((r) => (r.id === id ? { ...r, receipt_email: data.receipt_email } : r)));
      const buyer = reservations.find((r) => r.id === id);
      if (data.receipt_email === "sent") {
        toast.success(`Receipt email re-sent to ${buyer?.email || "the buyer"}.`);
      } else {
        toast.error("Receipt email failed again. Check email settings.");
      }
    } catch (e) {
      toast.error("Could not resend the receipt.");
    } finally {
      setResendingId("");
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm("Delete this reservation? This frees up their share on the counter.")) return;
    try {
      await authed.delete(`${API}/admin/reservations/${id}`);
      setReservations((rs) => rs.filter((r) => r.id !== id));
      const { data } = await axios.get(`${API}/availability`);
      setAvailability(data);
      setSlots(String(data.slots_total));
    } catch (e) {
      setError("Could not delete reservation.");
    }
  };

  const deleteWaitlist = async (id) => {
    if (!window.confirm("Remove this person from the waitlist?")) return;
    try {
      await authed.delete(`${API}/admin/waitlist/${id}`);
      setWaitlist((ws) => ws.filter((w) => w.id !== id));
    } catch (e) {
      setError("Could not remove waitlist entry.");
    }
  };

  const logout = () => {
    localStorage.removeItem("wf_token");
    setToken("");
    setUser(null);
  };

  const inputCls =
    "w-full rounded-xl border border-linen bg-cream px-4 py-3.5 text-base text-forest focus:outline-none focus:ring-2 focus:ring-forest/30 transition-[box-shadow] duration-200";

  if (!user) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={doLogin} className="bg-white rounded-2xl border border-linen p-10 max-w-md w-full" data-testid="admin-login-form">
          <Beef className="w-10 h-10 text-clay" strokeWidth={1.5} />
          <h1 className="font-display text-3xl tracking-tight font-medium mt-5">Manager login</h1>
          <p className="text-moss text-base mt-2">Waters & Field farm manager access.</p>
          <div className="mt-8 space-y-4">
            <input data-testid="admin-email-input" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="admin-password-input" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-clay text-sm mt-4" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="mt-8 w-full rounded-full bg-forest text-cream font-semibold px-8 py-4 hover:bg-forest/90 transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Log in
          </button>
          <Link to="/" className="block text-center text-moss text-sm mt-6 hover:text-forest" data-testid="admin-back-link">Back to site</Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream px-6 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Manager dashboard</h1>
            <p className="text-moss text-base mt-1">Logged in as {user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {stats && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-linen px-3.5 py-1.5 text-sm font-semibold text-forest" data-testid="site-views-stat" title="Website views — total, and today">
                <Eye className="w-4 h-4 text-clay" strokeWidth={1.5} />
                {stats.views_total.toLocaleString()} views
                <span className="text-moss font-normal">· {stats.views_today} today</span>
              </span>
            )}
            <Link to="/" className="text-moss text-sm hover:text-forest" data-testid="admin-back-site">View site</Link>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-linen bg-white px-5 py-2.5 text-sm font-semibold hover:border-forest/40 transition-colors duration-200">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-8 bg-white rounded-2xl border border-linen p-6" data-testid="views-chart-card">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
              <p className="text-base font-semibold">Website views — last 7 days</p>
              <p className="text-moss text-sm" data-testid="views-chart-total">{stats.views_total.toLocaleString()} total · {stats.views_today} today</p>
            </div>
            <div className="flex items-end gap-2 h-28">
              {stats.views_week.map((d) => {
                const max = Math.max(...stats.views_week.map((x) => x.views), 1);
                const [y, m, dd] = d.date.split("-").map(Number);
                const label = new Date(y, m - 1, dd).toLocaleDateString("en-US", { weekday: "short" });
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 justify-end h-full" title={`${label}: ${d.views} view${d.views === 1 ? "" : "s"}`} data-testid={`views-bar-${d.date}`}>
                    <span className="text-xs font-semibold text-forest">{d.views > 0 ? d.views : ""}</span>
                    <div className={`w-full rounded-t-md ${d.views > 0 ? "bg-clay/70" : "bg-forest/10"}`} style={{ height: `${Math.max((d.views / max) * 72, 3)}px` }} />
                    <span className="text-xs text-moss">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {availability && (
          <div className="mt-8 bg-white rounded-2xl border border-linen p-6 flex flex-wrap items-center gap-4" data-testid="availability-editor">
            <Beef className="w-6 h-6 text-clay" strokeWidth={1.5} />
            <p className="text-base">
              <span className="font-semibold">{availability.remaining}</span> of {availability.slots_total} steers remaining this season
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <input type="number" min="0" max="100" value={slots} onChange={(e) => setSlots(e.target.value)}
                data-testid="slots-input" className="w-24 rounded-lg border border-linen bg-cream px-3 py-2 text-base" />
              <button onClick={saveSlots} data-testid="slots-save-btn"
                className="rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-forest/90 transition-colors duration-200">
                Set total steers
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 bg-white rounded-2xl border border-linen p-6" data-testid="pricing-editor">
          <p className="text-base font-semibold mb-4">Pricing — updates the website instantly</p>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-5">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-moss mb-2">Price per pound (hanging weight)</p>
              <div className="flex flex-wrap items-end gap-4">
                {[["quarter", "Quarter"], ["half", "Half"], ["whole", "Whole"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-moss block mb-1.5">{label} $/lb</label>
                    <input
                      type="number" step="0.01" min="0.01" max="50"
                      value={prices[key]}
                      onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
                      data-testid={`price-input-${key}`}
                      className="w-28 rounded-lg border border-linen bg-cream px-3 py-2 text-base"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-moss mb-2">Deposit (required, non-refundable)</p>
              <div className="flex flex-wrap items-end gap-4">
                {[["deposit_quarter", "Quarter"], ["deposit_half", "Half"], ["deposit_whole", "Whole"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-moss block mb-1.5">{label} deposit $</label>
                    <input
                      type="number" step="0.01" min="0.01" max="10000"
                      value={prices[key]}
                      onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
                      data-testid={`deposit-input-${key.replace("deposit_", "")}`}
                      className="w-28 rounded-lg border border-linen bg-cream px-3 py-2 text-base"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-moss mb-2">Typical range line (shown on the pricing cards)</p>
              <div className="flex flex-wrap items-end gap-4">
                {[["typical_quarter", "Quarter"], ["typical_half", "Half"], ["typical_whole", "Whole"]].map(([key, label]) => (
                  <div key={key} className="flex-1 min-w-[220px]">
                    <label className="text-xs text-moss block mb-1.5">{label} card line</label>
                    <input
                      type="text" maxLength={160}
                      value={prices[key]}
                      onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
                      data-testid={`typical-input-${key.replace("typical_", "")}`}
                      className="w-full rounded-lg border border-linen bg-cream px-3 py-2 text-base"
                    />
                  </div>
                ))}
              </div>
            </div>
            <button onClick={savePrices} data-testid="prices-save-btn"
              className="rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-forest/90 transition-colors duration-200">
              Save pricing
            </button>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-display text-2xl tracking-tight font-medium flex items-center gap-2">
            <Users className="w-5 h-5 text-clay" strokeWidth={1.5} /> Reservations ({reservations.length})
          </h2>
          <div className="mt-4 bg-white rounded-2xl border border-linen overflow-x-auto" data-testid="reservations-table">
            <table className="w-full text-left text-base min-w-[720px]">
              <thead>
                <tr className="border-b border-linen text-xs tracking-[0.15em] uppercase text-moss">
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Share</th>
                  <th className="px-5 py-4">Notes</th>
                  <th className="px-5 py-4">Deposit</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-8 text-moss">No reservations yet.</td></tr>
                )}
                {reservations.map((r) => (
                  <tr key={r.id} className="border-b border-linen last:border-0 align-top" data-testid={`reservation-row-${r.id.slice(0, 8)}`}>
                    <td className="px-5 py-4 font-medium">{r.name}</td>
                    <td className="px-5 py-4 text-moss">{r.email}<br />{r.phone}</td>
                    <td className="px-5 py-4 capitalize">{r.share}</td>
                    <td className="px-5 py-4 text-moss">{r.notes || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      {r.status === "deposit_paid" ? (
                        <div className="flex flex-col items-start gap-1">
                          <span className="inline-flex rounded-full bg-forest/10 text-forest text-sm font-semibold px-3.5 py-1.5" data-testid={`paid-badge-${r.id.slice(0, 8)}`}>Paid</span>
                          {r.receipt_email === "sent" && (
                            <span className="inline-flex items-center gap-1 text-xs text-moss" data-testid={`receipt-sent-${r.id.slice(0, 8)}`}>
                              <MailCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> receipt emailed
                            </span>
                          )}
                          {r.receipt_email === "failed" && (
                            <span className="inline-flex items-center gap-1.5 text-xs text-clay" data-testid={`receipt-failed-${r.id.slice(0, 8)}`}>
                              <MailWarning className="w-3.5 h-3.5" strokeWidth={1.5} /> receipt failed
                              <button
                                type="button"
                                onClick={() => resendReceipt(r.id)}
                                disabled={resendingId === r.id}
                                data-testid={`resend-receipt-${r.id.slice(0, 8)}`}
                                className="underline font-semibold hover:text-claydark transition-colors duration-200 disabled:opacity-50 disabled:no-underline"
                              >
                                {resendingId === r.id ? "Sending…" : "Resend"}
                              </button>
                            </span>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => markPaid(r.id)}
                          data-testid={`mark-paid-${r.id.slice(0, 8)}`}
                          className="rounded-full bg-clay text-cream text-sm font-semibold px-4 py-2 hover:bg-claydark transition-colors duration-200"
                        >
                          Mark paid
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4 text-moss whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteReservation(r.id)}
                        data-testid={`delete-reservation-${r.id.slice(0, 8)}`}
                        className="text-moss/60 hover:text-clay transition-colors duration-200"
                        title="Delete reservation"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-10 mb-16">
          <h2 className="font-display text-2xl tracking-tight font-medium flex items-center gap-2">
            <Clock className="w-5 h-5 text-clay" strokeWidth={1.5} /> Waitlist ({waitlist.length})
          </h2>
          <div className="mt-4 bg-white rounded-2xl border border-linen overflow-x-auto" data-testid="waitlist-table">
            <table className="w-full text-left text-base min-w-[640px]">
              <thead>
                <tr className="border-b border-linen text-xs tracking-[0.15em] uppercase text-moss">
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Share wanted</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {waitlist.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-8 text-moss">No one on the waitlist yet.</td></tr>
                )}
                {waitlist.map((w) => (
                  <tr key={w.id} className="border-b border-linen last:border-0" data-testid={`waitlist-row-${w.id.slice(0, 8)}`}>
                    <td className="px-5 py-4 font-medium">{w.name}</td>
                    <td className="px-5 py-4 text-moss">{w.email}<br />{w.phone}</td>
                    <td className="px-5 py-4 capitalize">
                      {w.share}
                      {w.status === "notified" && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-forest/10 text-forest text-xs font-semibold px-2.5 py-1 normal-case" data-testid={`waitlist-notified-${w.id.slice(0, 8)}`}>
                          <MailCheck className="w-3 h-3" strokeWidth={1.5} /> notified
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-moss whitespace-nowrap">{new Date(w.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteWaitlist(w.id)}
                        data-testid={`delete-waitlist-${w.id.slice(0, 8)}`}
                        className="text-moss/60 hover:text-clay transition-colors duration-200"
                        title="Remove from waitlist"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
